/**
 * THE SHARED VOCABULARY — the closed lists more than one contract has to name.
 *
 * They live in a leaf module for the reason `primitives.ts` does: the work
 * contracts and the intent contracts both need them, and a module that
 * imported them from the other would make the two import each other. A cycle
 * in a contract package does not fail loudly — it resolves to `undefined` at
 * load time and the package dies on its first `.nullable()`.
 *
 * Nothing here is new. Each of these was defined beside the first contract
 * that needed it and moved when a second one did.
 */
import { z } from "zod";
/** The only tone vocabulary. A marker points; the WORDS carry the state. */
export const Tone = z.enum(["neutral", "brand", "warn", "danger", "ok"]);
/**
 * The role a record plays in the work — declared when it is enclosed, and the
 * signal that lets everything else be inferred rather than asked.
 */
export const RecordRole = z.enum(["TRIAL_BALANCE", "VENDOR_BALANCES", "VENDOR_LEDGER", "GENERAL_LEDGER", "CASH_BOOK", "BANK_STATEMENT",
    /** ADR-0019 F — the client's sales ledger / invoice register, for account preparation. */
    "SALES_LEDGER",
    /** R10.1 — supplied by the client through a request link; no capability declares it, so it is never enclosed any other way. */
    "CLIENT_RESPONSE"]);
/**
 * A VALUE AND ITS WORD. Any closed vocabulary a screen has to render arrives
 * like this, because the alternative is a second copy of the words in the
 * browser — which is how ANNUAL_ACCOUNTS ends up printed at an accountant.
 */
export const Named = z.object({ value: z.string(), label: z.string() });
/**
 * THE KINDS OF RECORD, with their words.
 *
 * A screen has to name a kind of record BEFORE one is enclosed — in the
 * control that asks for it, and in the list of what is still missing — so
 * there is no `roleLabel` to read from an existing record. Without this the
 * browser would have to keep its own copy of the words, which is how
 * TRIAL_BALANCE ends up printed at an accountant.
 */
export const RecordKind = z.object({
    role: RecordRole,
    label: z.string(),
    /** What this record is FOR, in one line, where a screen has room to say it. */
    purpose: z.string(),
});
/**
 * THE CONTROL ACCOUNT — the one genuinely human decision in this capability,
 * and even it is a pick from records already in hand rather than free text.
 * The server proposes from the admitted trial balance; the accountant confirms.
 */
export const ControlCandidate = z.object({
    /**
     * THE SERVER'S OWN IDENTITY FOR THIS LINE (L3-CTL-002).
     *
     * Derived from the admitted SourceVersion, the sheet and the row — the three
     * things that together ARE the line. Stable for as long as that record is
     * the record, because the canonical extraction is pinned at admission; a
     * corrected trial balance admitted later yields new identities, which is
     * correct, because those are different lines on a different record.
     *
     * It is the only thing `/start` accepts. A label and a balance side supplied
     * by a caller are a caller defining the professional scope of the
     * reconciliation, and it is not theirs to define.
     */
    id: z.string().min(16).max(64),
    label: z.string(),
    statementLabelContains: z.string(),
    balanceSide: z.enum(["CREDIT_NORMAL", "DEBIT_NORMAL"]),
    /** The side in words. The enum stays available for the professional record. */
    balanceSideLabel: z.string(),
    sheetName: z.string(),
    rowIndex: z.number().int().nonnegative(),
    amountText: z.string().nullable(),
    proposed: z.boolean(),
    /** Why this line alone matched, or null when Leveri proposed nothing. */
    proposedBecause: z.string().nullable().default(null),
    /** The client's own non-empty row cells, in order, for inspection. */
    rowText: z.array(z.string()).default([]),
});
//# sourceMappingURL=vocabulary.js.map